import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, Signal, signal, untracked, WritableSignal } from '@angular/core';
import {
    BackgroundColorDirective,
    ButtonDirective,
    ColorDirective,
    DCColor,
    DCColorInput,
    LoadingSpinnerComponent,
    TypographyDirective,
} from '@dartcounter/ui';
import { AlertAction, AlertPayload, AlertService } from '@dc-core/dc-services/alert.service';
import _ from 'lodash';
import { BetaFlagComponent } from 'src/app/modules/account-settings/components/beta-flag/beta-flag.component';
import { OldIconComponent } from 'src/app/shared/components/icon/icon.component';
import { getDcColor } from 'src/app/shared/utils/color.utils';

type ExtraAlertPayload = AlertPayload & { id: string };

@Component({
    selector: 'app-alerts',
    templateUrl: 'alerts.component.html',
    styleUrls: ['alerts.component.css'],
    imports: [
        ColorDirective,
        BackgroundColorDirective,
        ButtonDirective,
        OldIconComponent,
        CommonModule,
        TypographyDirective,
        LoadingSpinnerComponent,
        BetaFlagComponent,
    ],
    standalone: true,
})
export class AlertsComponent implements OnInit {
    private alertService: AlertService = inject(AlertService);

    public currentAlert: WritableSignal<ExtraAlertPayload> = signal(null);

    public backgroundColor: Signal<DCColorInput> = computed(
        () => this.currentAlert()?.backgroundColor ?? 'flight-blue'
    );
    public titleColor: Signal<DCColorInput> = computed(() =>
        this.currentAlert()?.titleColor === null ? null : (this.currentAlert()?.titleColor ?? 'dark-treble-green-2')
    );
    public textColor: Signal<DCColorInput> = computed(() =>
        this.currentAlert()?.textColor === null ? null : (this.currentAlert()?.textColor ?? 'dark-treble-green-2')
    );
    public closeButtonColor: Signal<DCColorInput> = computed(
        () => this.currentAlert()?.closeButtonColor ?? this.titleColor()
    );
    public confirmButtonColor: Signal<DCColorInput> = computed(
        () => this.currentAlert()?.confirmButtonColor ?? this.dcConfirmColor() ?? 'dark-treble-green'
    );
    public confirmButtonTextColor: Signal<DCColorInput | null> = computed(
        () => this.currentAlert()?.confirmButtonTextColor ?? null
    );
    public cancelButtonColor: Signal<DCColorInput> = computed(
        () => this.currentAlert()?.cancelButtonColor ?? 'dark-treble-green'
    );
    public cancelButtonTextColor: Signal<DCColorInput> = computed(
        () => this.currentAlert()?.cancelButtonTextColor ?? this.cancelButtonColor()
    );
    public dcConfirmColor: Signal<DCColor> = computed(() => getDcColor(this.currentAlert()?.confirmColor));

    public ngOnInit(): void {
        this.alertService.$alerts.subscribe((alert: ExtraAlertPayload) => {
            if (alert) {
                if (alert.id) {
                    if (alert.id === this.currentAlert()?.id) {
                        return;
                    }
                } else {
                    alert.id = Math.random().toString(36).substring(2, 8);
                }

                if (!alert.icon) {
                    alert.icon = null;
                }

                if (!alert.confirmColor) {
                    alert.confirmColor = 'black';
                }

                if (!alert.showCloseButton) {
                    alert.showCloseButton = false;
                }

                if (alert.timer == null) {
                    alert.timer = null;
                }

                untracked(() => {
                    this.currentAlert.set(alert);
                });

                if (alert.timer) {
                    if (alert.timer === 'default') {
                        alert.timer = 1500;
                    }

                    setTimeout(() => {
                        this.dismissAlertById(alert.id);
                    }, alert.timer);
                }
            } else {
                untracked(() => {
                    this.currentAlert.set(null);
                });
            }
        });

        this.alertService.$alertActions.subscribe((action: AlertAction) => {
            this.dismissAlertById(action.id);
        });
    }

    public dismissAlert(confirmed: boolean = null): void {
        const alert = _.cloneDeep(this.currentAlert());
        untracked(() => {
            this.currentAlert.set(null);
        });

        setTimeout(() => {
            if (confirmed === true && alert.onConfirm) {
                alert.onConfirm();
            } else if (confirmed === false && alert.onCancel) {
                alert.onCancel();
            } else if (alert.onClose) {
                alert.onClose();
            }
        }, 300);
    }

    public checkBackdropDismiss(): void {
        if (this.currentAlert()?.backdropDismiss === true) {
            this.dismissAlert();
        }
    }

    private dismissAlertById(id: string): void {
        if (this.currentAlert()?.id === id) {
            this.dismissAlert();
        }
    }
}
